import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle, Ban, CalendarDays, CheckCircle2, ClipboardCheck, ClipboardList,
    FileText, HardHat, Loader2, LogOut, MapPin, Receipt, RefreshCw, ShieldCheck, User, X,
} from 'lucide-react';
import { Button } from '../../Button';
import { describeError } from '../../../services/errorMessage';
import {
    ASSIGNMENT_STATUS_KLASSEN,
    ASSIGNMENT_STATUS_LABELS,
    AssignmentStatus,
    InspectionAssignment,
    formatZeitraum,
    zeitraumTage,
} from '../../../services/assignmentLogic';
import {
    Freelancer,
    FreelancerQualification,
    QUALI_STATUS_KLASSEN,
    QUALI_STATUS_LABELS,
    QUALI_TYP_LABELS,
    formatAnschrift,
    formatCents,
    heuteIso,
} from '../../../services/freelancerLogic';
import {
    PortalProjekt,
    auftraegeJeProjekt,
    freelancerAktionen,
    gruppiereAuftraege,
    nachweisWarnung,
    offeneAngeboteZahl,
    portalNachweise,
    portalZugang,
    projektAdresse,
    sortiereProjekte,
} from '../../../services/freelancerPortalLogic';
import {
    fetchMeineAuftraege,
    fetchMeineProjekte,
    fetchMeineQualifikationen,
    fetchMeinFreelancer,
    setzeAuftragsStatus,
} from '../../../services/freelancerPortalService';
import { kannBegehungBearbeiten } from '../../../services/freelancerInspectionLogic';
import { FreelancerCreditNote } from '../../../services/freelancerCreditNoteLogic';
import { fetchMeineGutschriften } from '../../../services/freelancerCreditNoteService';
import { FreelancerCreditNotes } from './FreelancerCreditNotes';
import { FreelancerInspection } from './FreelancerInspection';
import { begehungVerlassen } from './begehungVerlassenLogik';
import { BauphaseBanner } from '../../bauphase/BauphaseBanner';
import type { MaintenanceState } from '../../../services/maintenanceLogic';

// Das Freelancer-Portal (ERP/CRM-Plan Abschnitt 7 „Workflow" Schritte 2–3 und
// 3.4 — Phase D3).
//
// Duenner Container (Standing Decision): laedt vier Dinge EINMAL (Stammsatz,
// Nachweise, Auftraege, Projekte) und reicht sie durch. Jede fachliche Aussage
// — welche Knoepfe erscheinen, wie die Ampel steht, was „nicht freigeschaltet"
// heisst — kommt fertig aus freelancerPortalLogic.ts.
//
// ─── Die Begehung selbst (D4) ──────────────────────────────────────────────
// Ein Auftrag im Status angenommen / durchgefuehrt / nachbesserung traegt den
// Knopf „Begehung durchfuehren" und oeffnet `FreelancerInspection`. Die
// Kindtabellen der Begehung bleiben fuer die Rolle `freelancer` weiterhin
// gesperrt — der Zugriff laeuft ausschliesslich ueber die beiden
// SECURITY-DEFINER-RPCs aus supabase/freelancer_inspection.sql (Nr. 21).
//
// Stammdaten und Nachweise sind LESEND. Der Satz je Begehung steht in einem
// Vertrag, und ein selbst gesetztes Ablaufdatum am Haftpflichtnachweis hebt
// genau die Sperre auf, die er ausloesen soll (Begruendung ausfuehrlich in
// freelancer_portal.sql, Abschnitt 5e).

type PortalBereich = 'auftraege' | 'projekte' | 'gutschriften' | 'profil';

const BEREICHE: { id: PortalBereich; label: string; icon: React.ReactNode }[] = [
    { id: 'auftraege', label: 'Meine Aufträge', icon: <ClipboardList className="w-4 h-4" /> },
    { id: 'projekte', label: 'Meine Projekte', icon: <MapPin className="w-4 h-4" /> },
    { id: 'gutschriften', label: 'Meine Gutschriften', icon: <Receipt className="w-4 h-4" /> },
    { id: 'profil', label: 'Mein Profil', icon: <User className="w-4 h-4" /> },
];

/** Anzeigetext der Knoepfe. Nur diese beiden Ziele gibt es fuer den
 *  Freelancer (assignmentLogic.ASSIGNMENT_TRANSITIONS). */
const AKTIONS_LABELS: Partial<Record<AssignmentStatus, string>> = {
    angenommen: 'Annehmen',
    abgelehnt: 'Ablehnen',
};

interface Props {
    /** Abmelden — dieselbe Funktion wie in der Sidebar der internen Ansicht. */
    onLogout: () => void;
    /** Anzeigename aus dem Profil, solange der Stammsatz noch fehlt. */
    anzeigeName?: string;
    /**
     * Bauphase (useBauphaseGate in App.tsx): das Portal hat keine Sidebar und
     * kein <main> der internen Ansicht, also bekommt es die Countdown-Leiste
     * hier gereicht. Ohne sie saehe der Freelancer die Sperre erst, wenn die
     * Wartungsseite seinen Baum ersetzt.
     */
    bauphase?: { state: MaintenanceState };
}

const StatusAbzeichen: React.FC<{ status: AssignmentStatus }> = ({ status }) => (
    <span
        className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold border ${ASSIGNMENT_STATUS_KLASSEN[status]}`}
    >
        {ASSIGNMENT_STATUS_LABELS[status]}
    </span>
);

export const FreelancerPortal: React.FC<Props> = ({ onLogout, anzeigeName, bauphase }) => {
    const [freelancer, setFreelancer] = useState<Freelancer | null>(null);
    const [qualis, setQualis] = useState<FreelancerQualification[]>([]);
    const [auftraege, setAuftraege] = useState<InspectionAssignment[]>([]);
    const [projekte, setProjekte] = useState<PortalProjekt[]>([]);
    const [gutschriften, setGutschriften] = useState<FreelancerCreditNote[]>([]);
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [bereich, setBereich] = useState<PortalBereich>('auftraege');
    const [handeltAn, setHandeltAn] = useState<string | null>(null);
    /** Offene Begehung. Gesetzt = die Erfassungsmaske steht statt der Liste. */
    const [begehungAn, setBegehungAn] = useState<string | null>(null);

    const laden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            const eigener = await fetchMeinFreelancer();
            setFreelancer(eigener);
            if (!eigener) {
                setQualis([]);
                setAuftraege([]);
                setProjekte([]);
                setGutschriften([]);
                return;
            }
            // Drei unabhaengige Abfragen — nacheinander waeren es drei
            // Rundreisen fuer eine Seite, die ohnehin alles zeigt.
            const [meineQualis, meineAuftraege, meineProjekte] = await Promise.all([
                fetchMeineQualifikationen(eigener.id),
                fetchMeineAuftraege(eigener.id),
                fetchMeineProjekte(),
            ]);
            setQualis(meineQualis);
            setAuftraege(meineAuftraege);
            setProjekte(meineProjekte);

            // Gutschriften BEWUSST separat und mit verschlucktem Fehler:
            // laeuft die Migration Nr. 22 auf dieser Umgebung noch nicht,
            // darf das Portal deswegen nicht mit einem Ladefehler stehen —
            // Angebote annehmen und Begehungen fuehren ist die Arbeit, die
            // Abrechnung nur ihr Nachklang.
            try {
                setGutschriften(await fetchMeineGutschriften());
            } catch {
                setGutschriften([]);
            }
        } catch (e) {
            setFehler(describeError(e));
        } finally {
            setLaedt(false);
        }
    }, []);

    useEffect(() => {
        void laden();
    }, [laden]);

    const zugang = useMemo(() => portalZugang(freelancer), [freelancer]);
    const gruppen = useMemo(() => gruppiereAuftraege(auftraege), [auftraege]);
    const offene = useMemo(() => offeneAngeboteZahl(auftraege), [auftraege]);
    const sortierteProjekte = useMemo(() => sortiereProjekte(projekte), [projekte]);
    const projektNamen = useMemo(() => {
        const m = new Map<string, PortalProjekt>();
        for (const p of projekte) m.set(p.id, p);
        return m;
    }, [projekte]);
    const auftraegeProProjekt = useMemo(
        () => auftraegeJeProjekt(projekte, auftraege),
        [projekte, auftraege],
    );
    const heute = heuteIso();
    const nachweise = useMemo(() => portalNachweise(qualis, heute), [qualis, heute]);
    const warnung = useMemo(() => nachweisWarnung(nachweise), [nachweise]);

    const statusSetzen = async (a: InspectionAssignment, ziel: AssignmentStatus) => {
        setFehler(null);
        setHandeltAn(a.id);
        try {
            await setzeAuftragsStatus(a.id, ziel);
            // Neu laden statt lokal umzubiegen: die Datenbank entscheidet
            // verbindlich, und ein abgelehnter Auftrag kann Folgen haben, die
            // dieser Bildschirm nicht kennt.
            await laden();
        } catch (e) {
            setFehler(describeError(e));
        } finally {
            setHandeltAn(null);
        }
    };

    // ── Auftragszeile ────────────────────────────────────────────────────────
    const AuftragsKarte: React.FC<{ a: InspectionAssignment }> = ({ a }) => {
        const aktionen = freelancerAktionen(a, zugang);
        const projekt = projektNamen.get(a.projectId);
        const tage = zeitraumTage(a.zeitraumVon, a.zeitraumBis);
        return (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h4 className="font-bold text-slate-800 text-sm truncate">
                            {projekt?.name || 'Projekt (nicht mehr freigeschaltet)'}
                        </h4>
                        {projekt?.projectNumber && (
                            <p className="text-[11px] text-slate-400 font-mono">{projekt.projectNumber}</p>
                        )}
                    </div>
                    <StatusAbzeichen status={a.status} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div className="flex items-center gap-2 text-slate-600">
                        <CalendarDays className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                        <span>
                            {formatZeitraum(a.zeitraumVon, a.zeitraumBis)}
                            {tage ? ` (${tage} ${tage === 1 ? 'Tag' : 'Tage'})` : ''}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                        <FileText className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                        <span className="font-semibold">{formatCents(a.verguetungCents)}</span>
                    </div>
                    {projektAdresse(projekt) && (
                        <div className="flex items-center gap-2 text-slate-600 min-w-0">
                            <MapPin className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                            <span className="truncate">{projektAdresse(projekt)}</span>
                        </div>
                    )}
                </div>

                {(aktionen.length > 0 || kannBegehungBearbeiten(a)) && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {kannBegehungBearbeiten(a) && (
                            <Button
                                variant="primary"
                                onClick={() => setBegehungAn(a.id)}
                                className="text-xs py-1.5"
                                icon={<ClipboardCheck className="w-3.5 h-3.5" />}
                            >
                                {a.status === 'nachbesserung' ? 'Nachbesserung bearbeiten' : 'Begehung durchführen'}
                            </Button>
                        )}
                        {aktionen.map((ziel) => (
                            <Button
                                key={ziel}
                                variant={ziel === 'angenommen' ? 'primary' : 'secondary'}
                                onClick={() => statusSetzen(a, ziel)}
                                disabled={handeltAn === a.id}
                                className="text-xs py-1.5"
                                icon={
                                    handeltAn === a.id ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : ziel === 'angenommen' ? (
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                    ) : (
                                        <Ban className="w-3.5 h-3.5" />
                                    )
                                }
                            >
                                {AKTIONS_LABELS[ziel] || ASSIGNMENT_STATUS_LABELS[ziel]}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // ── Zustand: Zugang noch nicht verknüpft ─────────────────────────────────
    if (!laedt && zugang.zustand === 'nicht_verknuepft') {
        return (
            <PortalRahmen onLogout={onLogout} name={anzeigeName} offene={0} bereich={bereich} setBereich={setBereich} nurKopf bauphase={bauphase}>
                <div className="max-w-xl mx-auto mt-10 bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center space-y-4">
                    <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto">
                        <ShieldCheck className="w-7 h-7 text-amber-600" />
                    </div>
                    <h2 className="text-lg font-bold text-slate-800">Ihr Zugang ist noch nicht freigeschaltet</h2>
                    <p className="text-sm text-slate-600 leading-relaxed">{zugang.hinweis}</p>
                    <Button variant="secondary" onClick={() => void laden()} icon={<RefreshCw className="w-4 h-4" />}>
                        Erneut prüfen
                    </Button>
                </div>
            </PortalRahmen>
        );
    }

    return (
        <PortalRahmen
            // Reiter und „Abmelden" verlassen eine offene Begehung: erst deren
            // Rückfrage bei ungespeicherten Änderungen (begehungVerlassenLogik).
            onLogout={() => begehungVerlassen(onLogout)}
            name={freelancer?.name || anzeigeName}
            offene={offene}
            bereich={bereich}
            setBereich={(b) => begehungVerlassen(() => { setBegehungAn(null); setBereich(b); })}
            bauphase={bauphase}
        >
            {fehler && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-3 text-xs flex items-start gap-2 mb-4">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="flex-1">{fehler}</span>
                    <button onClick={() => setFehler(null)} className="shrink-0 hover:bg-rose-100 rounded p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {zugang.hinweis && zugang.zustand !== 'nicht_verknuepft' && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 text-xs flex items-start gap-2 mb-4">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{zugang.hinweis}</span>
                </div>
            )}

            {laedt ? (
                <div className="flex items-center justify-center py-20 text-slate-400">
                    <Loader2 className="w-6 h-6 animate-spin" />
                </div>
            ) : begehungAn ? (
                // Die Erfassungsmaske ersetzt die Liste, statt sie zu ueberlagern:
                // eine Begehung wird am Handy gefuehrt, und ein Modal ueber einer
                // scrollenden Liste ist dort die schlechtere Ordnung.
                <FreelancerInspection
                    assignmentId={begehungAn}
                    onBack={() => setBegehungAn(null)}
                    onStatusChanged={() => { void laden(); }}
                />
            ) : bereich === 'auftraege' ? (
                <div className="space-y-8">
                    <section className="space-y-3">
                        <h3 className="text-sm font-bold text-slate-700">
                            Offene Angebote
                            {gruppen.angebote.length > 0 && (
                                <span className="ml-2 text-xs font-medium text-slate-400">
                                    {gruppen.angebote.length}
                                </span>
                            )}
                        </h3>
                        {gruppen.angebote.length === 0 ? (
                            <p className="text-xs text-slate-400 italic">
                                Zurzeit liegt Ihnen kein Angebot vor.
                            </p>
                        ) : (
                            gruppen.angebote.map((a) => <AuftragsKarte key={a.id} a={a} />)
                        )}
                    </section>

                    <section className="space-y-3">
                        <h3 className="text-sm font-bold text-slate-700">Laufende Aufträge</h3>
                        {gruppen.laufend.length === 0 ? (
                            <p className="text-xs text-slate-400 italic">Kein laufender Auftrag.</p>
                        ) : (
                            gruppen.laufend.map((a) => <AuftragsKarte key={a.id} a={a} />)
                        )}
                    </section>

                    {gruppen.erledigt.length > 0 && (
                        <section className="space-y-3">
                            <h3 className="text-sm font-bold text-slate-700">Abgeschlossen</h3>
                            {gruppen.erledigt.map((a) => <AuftragsKarte key={a.id} a={a} />)}
                        </section>
                    )}
                </div>
            ) : bereich === 'gutschriften' ? (
                <FreelancerCreditNotes belege={gutschriften} />
            ) : bereich === 'projekte' ? (
                <div className="space-y-4">
                    {sortierteProjekte.length === 0 ? (
                        <p className="text-xs text-slate-400 italic">
                            Sie sind derzeit für kein Projekt freigeschaltet.
                        </p>
                    ) : (
                        sortierteProjekte.map((p) => {
                            const dazu = auftraegeProProjekt.get(p.id) || [];
                            return (
                                <div key={p.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <h4 className="font-bold text-slate-800 text-sm">{p.name}</h4>
                                            {p.projectNumber && (
                                                <p className="text-[11px] text-slate-400 font-mono">{p.projectNumber}</p>
                                            )}
                                        </div>
                                        <span className="text-[11px] text-slate-400 shrink-0">
                                            {dazu.length} {dazu.length === 1 ? 'Auftrag' : 'Aufträge'}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                                        <div className="flex items-start gap-2 text-slate-600">
                                            <MapPin className="w-3.5 h-3.5 shrink-0 text-slate-400 mt-0.5" />
                                            <span>{projektAdresse(p) || 'Keine Adresse hinterlegt'}</span>
                                        </div>
                                        <div className="flex items-start gap-2 text-slate-600">
                                            <User className="w-3.5 h-3.5 shrink-0 text-slate-400 mt-0.5" />
                                            <span>
                                                {p.coordinatorName
                                                    ? `Ansprechpartner: ${p.coordinatorName}`
                                                    : 'Kein Ansprechpartner hinterlegt'}
                                            </span>
                                        </div>
                                    </div>

                                    {dazu.length > 0 && (
                                        <div className="flex flex-wrap gap-2 pt-1">
                                            {dazu.map((a) => (
                                                <span key={a.id} className="inline-flex items-center gap-2 text-[11px] text-slate-500">
                                                    <StatusAbzeichen status={a.status} />
                                                    {formatZeitraum(a.zeitraumVon, a.zeitraumBis)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
                        <h3 className="text-sm font-bold text-slate-700">Stammdaten</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <Feld label="Name" wert={freelancer?.name} />
                            <Feld label="Firma" wert={freelancer?.firma} />
                            <Feld
                                label="Anschrift"
                                wert={formatAnschrift(freelancer || undefined) || undefined}
                            />
                            <Feld label="Einsatzradius" wert={`${freelancer?.einsatzradiusKm ?? 50} km`} />
                            <Feld label="Satz je Begehung" wert={formatCents(freelancer?.satzJeBegehungCents)} />
                            <Feld
                                label="Status"
                                wert={freelancer?.status === 'aktiv' ? 'aktiv' : freelancer?.status}
                            />
                        </div>
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                            Diese Angaben pflegt der Innendienst — sie sind Bestandteil Ihrer Vereinbarung
                            und lassen sich hier nicht ändern. Änderungswünsche bitte an Ihre
                            Ansprechpartnerin oder Ihren Ansprechpartner.
                        </p>
                    </section>

                    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
                        <div className="flex items-center justify-between gap-3">
                            <h3 className="text-sm font-bold text-slate-700">Qualifikationsnachweise</h3>
                            {warnung && (
                                <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
                                    {warnung}
                                </span>
                            )}
                        </div>

                        {nachweise.length === 0 ? (
                            <p className="text-xs text-slate-400 italic">Es sind keine Nachweise hinterlegt.</p>
                        ) : (
                            <div className="space-y-2">
                                {nachweise.map((n) => (
                                    <div
                                        key={n.quali.id}
                                        className="flex items-center gap-3 border border-slate-200 rounded-xl px-4 py-2.5"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-slate-700 truncate">
                                                {QUALI_TYP_LABELS[n.quali.typ] || n.quali.typ}
                                                {n.pflicht && (
                                                    <span className="ml-2 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                        Pflicht
                                                    </span>
                                                )}
                                            </p>
                                            {n.quali.bezeichnung && (
                                                <p className="text-[11px] text-slate-400 truncate">{n.quali.bezeichnung}</p>
                                            )}
                                        </div>
                                        <span className="text-[11px] text-slate-500 shrink-0">
                                            {n.quali.gueltigBis
                                                ? `gültig bis ${n.quali.gueltigBis.split('-').reverse().join('.')}`
                                                : 'ohne Ablaufdatum'}
                                        </span>
                                        <span
                                            className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold border shrink-0 ${QUALI_STATUS_KLASSEN[n.status]}`}
                                        >
                                            {QUALI_STATUS_LABELS[n.status]}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <p className="text-[11px] text-slate-400 leading-relaxed">
                            Nachweise und Ablaufdaten pflegt ausschließlich der Innendienst — ein hier
                            selbst gesetztes Datum würde genau die Prüfung aufheben, die vor einer
                            Beauftragung stattfindet. Neue oder erneuerte Nachweise bitte an den
                            Innendienst senden; er hinterlegt sie und schaltet Sie danach wieder frei.
                        </p>
                    </section>
                </div>
            )}
        </PortalRahmen>
    );
};

// ─── Rahmen mit eigener Minimal-Navigation ──────────────────────────────────
//
// Bewusst NICHT die `Sidebar`: die kennt Dashboard, Projekte, Aufgaben, CRM,
// Angebote, Rechnungen und Admin-Konsole. Fuer die Rolle `freelancer` waere
// jeder dieser Eintraege ein Klick in eine Fehlermeldung — die Tabellen
// dahinter sind fuer ihn gesperrt. Statt die Sidebar um Rollenabfragen zu
// erweitern, bekommt das Portal seine eigene, dreiteilige Navigation.

const PortalRahmen: React.FC<{
    children: React.ReactNode;
    onLogout: () => void;
    name?: string;
    offene: number;
    bereich: PortalBereich;
    setBereich: (b: PortalBereich) => void;
    /** Bei „Zugang nicht freigeschaltet": Reiter ausblenden, es gibt nichts. */
    nurKopf?: boolean;
    bauphase?: { state: MaintenanceState };
}> = ({ children, onLogout, name, offene, bereich, setBereich, nurKopf, bauphase }) => (
    <div className="min-h-screen bg-gradient-to-br from-[#e0e7ff] via-[#f1f5f9] to-[#dbeafe]">
        <header className="bg-white/70 backdrop-blur border-b border-white/60 sticky top-0 z-10">
            {/* Leiste INNERHALB des klebenden Kopfes: beide sind sticky top-0,
                nebeneinander wuerde die Leiste (z-130) den Kopf beim Scrollen
                verdecken. Im Kopf bleiben Countdown und Navigation zusammen. */}
            {bauphase && bauphase.state.phase !== 'none' && <BauphaseBanner state={bauphase.state} />}
            <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
                    <HardHat className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-800 truncate">Freelancer-Portal</p>
                    {name && <p className="text-[11px] text-slate-500 truncate">{name}</p>}
                </div>
                <button
                    onClick={onLogout}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-white/70"
                >
                    <LogOut className="w-4 h-4" />
                    Abmelden
                </button>
            </div>

            {!nurKopf && (
                <nav className="max-w-4xl mx-auto px-4 flex gap-1 overflow-x-auto">
                    {BEREICHE.map((b) => (
                        <button
                            key={b.id}
                            onClick={() => setBereich(b.id)}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold border-b-2 whitespace-nowrap transition-colors ${
                                bereich === b.id
                                    ? 'border-blue-600 text-blue-700'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {b.icon}
                            {b.label}
                            {b.id === 'auftraege' && offene > 0 && (
                                <span className="ml-1 bg-blue-600 text-white rounded-full px-1.5 py-0.5 text-[11px] leading-none">
                                    {offene}
                                </span>
                            )}
                        </button>
                    ))}
                </nav>
            )}
        </header>

        <main className="max-w-4xl mx-auto px-4 py-6">{children}</main>
    </div>
);

const Feld: React.FC<{ label: string; wert?: string | null }> = ({ label, wert }) => (
    <div className="space-y-1">
        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{label}</p>
        <p className="text-sm text-slate-700">{wert || '—'}</p>
    </div>
);
